import { useEffect, useState } from "react";
import { CalendarDays, Clock, Users, Globe, Bot } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { STATUS_LABELS, STATUS_COLORS, SOURCE_LABELS } from "@/lib/agendaConstants";
import { PATIENTS } from "@/constants/testIds";

const SOURCE_ICONS = { manual: Users, public_booking: Globe, whatsapp_agent: Bot };

/** Historial (pasados) y próximos turnos del paciente, tomados de Agenda. */
export default function PatientAppointmentsTab({ patientId }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/patients/${patientId}/appointments`);
        setAppointments(data);
      } catch (err) {
        toast({ title: "Error al cargar turnos", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  if (loading) return <p className="text-muted-foreground text-sm">Cargando...</p>;

  if (appointments.length === 0) {
    return (
      <div className="bg-white border border-border rounded-xl p-8 text-center max-w-3xl" data-testid={PATIENTS.appointmentsEmptyState}>
        <CalendarDays className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">Este paciente todavía no tiene turnos.</p>
      </div>
    );
  }

  const now = new Date();

  return (
    <div className="space-y-3 max-w-3xl" data-testid={PATIENTS.appointmentsList}>
      {appointments.map((a) => {
        const start = new Date(a.start_time);
        const colors = STATUS_COLORS[a.status];
        const SourceIcon = SOURCE_ICONS[a.source] || Users;
        return (
          <div
            key={a.id}
            className="bg-white border border-border rounded-xl p-4 flex items-center justify-between gap-4"
            data-testid={`${PATIENTS.appointmentItemPrefix}${a.id}`}
          >
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">{a.service_name}</p>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="w-3.5 h-3.5" />
                <span className="capitalize">
                  {`${start.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })} \u00b7 ${start.toTimeString().slice(0, 5)}`}
                </span>
                <span>&middot; {a.professional_name}</span>
                {start < now && <span className="text-muted-foreground/70">(pasado)</span>}
              </div>
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground/80" data-testid={`${PATIENTS.appointmentOriginPrefix}${a.id}`}>
                <SourceIcon className="w-3 h-3" /> {SOURCE_LABELS[a.source] || SOURCE_LABELS.manual}
              </div>
            </div>
            <span
              className="text-xs font-medium px-2.5 py-1 rounded-full border shrink-0"
              style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}
            >
              {STATUS_LABELS[a.status]}
            </span>
          </div>
        );
      })}
    </div>
  );
}
